"use client";

import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { ArrowLeft, ArrowRight, Pause, Play } from "lucide-react";

import { formatCopy } from "@/lib/copy-format";

import { CcTestimonialPicture, type TestimonialPictureSources } from "./CcTestimonialPicture";

type FolioItem = {
  name: string;
  role: string;
  quote: string;
  image: string;
  imageSources?: TestimonialPictureSources;
  imagePosition: string;
  imageCrop?: string;
  imageAlt: string;
  logo?: string;
  logoSources?: TestimonialPictureSources;
  logoOnDark?: boolean;
};

type CcClientTestimonialsFolioProps = {
  items: FolioItem[];
  labels: {
    previous: string;
    next: string;
    pause: string;
    play: string;
    clients: string;
    counter: string;
  };
};

const PANEL_ID = "client-testimonials-story";
const SWIPE_THRESHOLD = 48;

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * One client story at a time. The active selector's progress line is a CSS
 * animation; its end advances the story, so pausing is just pausing the
 * animation. Autoplay stops while off screen, paused by the
 * visitor or when reduced motion is preferred. Every story stays mounted in
 * one grid cell, so the panel keeps the height of the longest quote.
 */
export function CcClientTestimonialsFolio({ items, labels }: CcClientTestimonialsFolioProps) {
  const [active, setActive] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const swipeStart = useRef<number | null>(null);

  const count = items.length;
  const go = (index: number) => setActive((index + count) % count);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), {
      threshold: 0.35,
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const tab = tabRefs.current[active];
    const row = tab?.parentElement;
    if (!tab || !row) return;
    const revealActiveTab = () => {
      if (row.scrollWidth <= row.clientWidth) return;
      const tabBounds = tab.getBoundingClientRect();
      const rowBounds = row.getBoundingClientRect();
      const delta = tabBounds.left < rowBounds.left
        ? tabBounds.left - rowBounds.left
        : tabBounds.right > rowBounds.right ? tabBounds.right - rowBounds.right : 0;
      if (delta) row.scrollTo({ left: row.scrollLeft + delta, behavior: reducedMotion ? "instant" : "smooth" });
    };
    revealActiveTab();
    const observer = new ResizeObserver(revealActiveTab);
    observer.observe(row);
    return () => observer.disconnect();
  }, [active, reducedMotion]);

  const autoplay = !reducedMotion && !userPaused;
  const running = autoplay && inView;

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const target =
      event.key === "ArrowRight" ? index + 1 : event.key === "ArrowLeft" ? index - 1 : event.key === "Home" ? 0 : event.key === "End" ? count - 1 : null;
    if (target === null) return;
    event.preventDefault();
    const next = (target + count) % count;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "mouse") swipeStart.current = event.clientX;
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (swipeStart.current === null) return;
    const delta = event.clientX - swipeStart.current;
    swipeStart.current = null;
    if (Math.abs(delta) >= SWIPE_THRESHOLD) go(active + (delta < 0 ? 1 : -1));
  };

  return (
    <div
      ref={rootRef}
      className="cc-client-testimonials__folio"
      data-running={running ? "true" : "false"}
      data-autoplay={autoplay ? "true" : "false"}
    >
      <div
        className="cc-client-testimonials__stage"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (swipeStart.current = null)}
      >
        <div className="cc-client-testimonials__portrait">
          {items.map((item, index) => (
            <div key={item.name} className="cc-client-testimonials__shot" data-active={index === active ? "true" : "false"}>
              <CcTestimonialPicture
                src={item.image}
                sources={item.imageSources}
                alt={index === active ? item.imageAlt : ""}
                sizes={item.imageCrop === "left-half" ? "(min-width: 900px) 68vw, 200vw" : "(min-width: 900px) 34vw, 100vw"}
                crop={item.imageCrop}
                style={{ objectPosition: item.imagePosition }}
              />
            </div>
          ))}
        </div>

        <div
          id={PANEL_ID}
          role="tabpanel"
          className="cc-client-testimonials__panel"
          aria-live={running ? "off" : "polite"}
        >
          <p className="cc-client-testimonials__count" aria-hidden="true">
            {formatCopy(labels.counter, { current: pad(active + 1), total: pad(count) })}
          </p>

          <div className="cc-client-testimonials__stories">
            {items.map((item, index) => (
              <figure
                key={item.name}
                className="cc-client-testimonials__story"
                data-active={index === active ? "true" : "false"}
                aria-hidden={index === active ? undefined : true}
                inert={index !== active}
              >
                <blockquote className="cc-client-testimonials__quote">
                  <p>{item.quote}</p>
                </blockquote>
                <figcaption className="cc-client-testimonials__person">
                  <span className="cc-client-testimonials__who">
                    <strong>{item.name}</strong>
                    <span>{item.role}</span>
                  </span>
                  {item.logo ? (
                    // Decorative: the role line already names the company.
                    <span className="cc-client-testimonials__logo" data-on-dark={item.logoOnDark ? "true" : undefined}>
                      <span className="cc-client-testimonials__logo-mark">
                        <CcTestimonialPicture src={item.logo} sources={item.logoSources} alt="" sizes="112px" />
                      </span>
                    </span>
                  ) : null}
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="cc-client-testimonials__controls">
            {count > 1 ? (
              <>
                <button
                  type="button"
                  className="cc-client-testimonials__control"
                  aria-label={autoplay ? labels.pause : labels.play}
                  aria-pressed={!autoplay}
                  disabled={reducedMotion}
                  onClick={() => setUserPaused((paused) => !paused)}
                >
                  {autoplay ? <Pause size={16} aria-hidden="true" /> : <Play size={16} aria-hidden="true" />}
                </button>
                <span className="cc-client-testimonials__arrows">
                  <button type="button" className="cc-client-testimonials__control" aria-label={labels.previous} aria-controls={PANEL_ID} onClick={() => go(active - 1)}>
                    <ArrowLeft size={18} aria-hidden="true" />
                  </button>
                  <button type="button" className="cc-client-testimonials__control" aria-label={labels.next} aria-controls={PANEL_ID} onClick={() => go(active + 1)}>
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="cc-client-testimonials__clients" role="tablist" aria-label={labels.clients}>
        {items.map((item, index) => (
          <button
            key={item.name}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={index === active}
            aria-controls={PANEL_ID}
            tabIndex={index === active ? 0 : -1}
            className="cc-client-testimonials__client"
            onClick={() => setActive(index)}
            onKeyDown={(event) => onTabKeyDown(event, index)}
          >
            <span className="cc-client-testimonials__avatar">
              <CcTestimonialPicture src={item.image} sources={item.imageSources} alt="" sizes={item.imageCrop === "left-half" ? "80px" : "40px"} crop={item.imageCrop} style={{ objectPosition: item.imagePosition }} />
            </span>
            <span className="cc-client-testimonials__client-text">
              <strong>{item.name}</strong>
              <span>{item.role}</span>
            </span>
            <span className="cc-client-testimonials__progress" aria-hidden="true">
              {index === active ? (
                <span key={active} className="cc-client-testimonials__progress-fill" onAnimationEnd={() => go(active + 1)} />
              ) : null}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
